import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '@/common/utils/utils'
import { Info } from 'lucide-react'
import { useIsSmallScreen } from './useIsSmallScreen'
import { Sheet, SheetContent, SheetTrigger } from './sheet'

const TooltipProvider = TooltipPrimitive.Provider

type TooltipProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
    enableOverlay?: boolean
    onOpenChange?: (open: boolean) => void
    clickTrigger?: boolean
}

const Tooltip: React.FC<TooltipProps> = ({ enableOverlay = false, clickTrigger = false, children, ...rootProps }) => {
    const { open, onOpenChange } = rootProps

    // If overlay mode is enabled, don't forward parent's `onOpenChange` so
    // Radix won't call back on hover/focus; parent should control `open`.
    const primitiveRootProps = enableOverlay
        ? ((): any => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { onOpenChange: _omit, ...rest } = rootProps as any
              return rest
          })()
        : rootProps

    const childArray = React.Children.toArray(children)
    const triggerChild = childArray[0] as React.ReactElement | undefined
    const restChildren = childArray.slice(1)

    const tooltipRootProps = {
        ...primitiveRootProps,
        ...(clickTrigger && { delayDuration: 0 }),
    }

    return (
        <TooltipPrimitive.Root {...tooltipRootProps}>
            {triggerChild && clickTrigger ? (
                <TooltipPrimitive.Trigger asChild onClick={(e) => e.stopPropagation()}>
                    {triggerChild}
                </TooltipPrimitive.Trigger>
            ) : (
                triggerChild && <TooltipPrimitive.Trigger asChild>{triggerChild}</TooltipPrimitive.Trigger>
            )}
            {restChildren}
            {enableOverlay && open && (
                <div
                    className="fixed inset-0 bg-black/30 z-40"
                    aria-hidden="true"
                    onClick={onOpenChange ? () => onOpenChange(false) : undefined}
                />
            )}
        </TooltipPrimitive.Root>
    )
}

const TooltipTrigger = TooltipPrimitive.Trigger

interface ContentProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
    arrowClassName?: string
    hideArrow?: boolean
}
const TooltipContent = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, ContentProps>(
    ({ className, arrowClassName, hideArrow, sideOffset = 4, ...props }, ref) => (
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                'z-50 overflow-hidden rounded-md border bg-stone-50 px-3 py-1.5 text-sm text-content-primary shadow-md',
                'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                className,
            )}
            {...props}
        >
            {props.children}
            {!hideArrow && <TooltipPrimitive.Arrow className={cn('w-4 h-2 fill-stone-50', arrowClassName)} />}
        </TooltipPrimitive.Content>
    ),
)
TooltipContent.displayName = TooltipPrimitive.Content.displayName

const InfoIconTooltip: React.FC<{
    children?: React.ReactNode
    content?: React.ReactNode
    size?: number
    infoClassName?: string
    contentClassName?: string
    onOpenChange?: (open: boolean) => void
}> = ({ children, content, size = 16, infoClassName: className, contentClassName, onOpenChange }) => {
    const isSmallScreen = useIsSmallScreen()
    const [open, setOpen] = React.useState(false)

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen)
        onOpenChange?.(newOpen)
    }

    const contentElement = content ? content : <p className={cn('md:w-60', contentClassName)}>{children}</p>

    if (isSmallScreen) {
        return (
            <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetTrigger asChild>
                    <button className="cursor-pointer focus:outline-none">
                        <Info size={size} strokeWidth={2} className={className} />
                    </button>
                </SheetTrigger>
                <SheetContent
                    side="bottom"
                    className="w-full px-4 py-6 rounded-t-2xl"
                    onClose={() => handleOpenChange(false)}
                >
                    <div className="flex flex-col gap-4">{contentElement}</div>
                </SheetContent>
            </Sheet>
        )
    }

    return (
        <TooltipProvider>
            <Tooltip open={open} onOpenChange={handleOpenChange} clickTrigger>
                <TooltipTrigger asChild>
                    <button className="cursor-pointer focus:outline-none">
                        <Info size={size} strokeWidth={2} className={className} />
                    </button>
                </TooltipTrigger>
                <TooltipContent arrowClassName="-mt-1" sideOffset={6} align="center">
                    {contentElement}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, InfoIconTooltip }
